export const meta = {
  title: 'Marketing broadcasts: reach everyone who said yes',
  updatedAt: '2026-07-17',
  related: ['promotions', 'notifications'],
};

A broadcast is a push notification you write once and send to a whole audience — a new promotion, a new package, holiday hours. It lands on your customers' phones through the same app they already installed from your QR.

## Composing

The **Marketing** page has your past campaigns on the left and the composer on the right. A campaign is a title, a message, an optional link to a screen in your app, and an audience. If you'd rather not stare at a blank box, the **AI draft** assist turns one sentence of intent into a title and message you can edit before saving.

## Audiences

Four ready-made audiences:

- **All customers** — everyone who has ever paid at your outlets.
- **Active last 30 days** — your warmest segment.
- **Silver tier+** — loyal customers worth rewarding.
- **Gold tier only** — VIP-only offers.

One honest note about "all": your reachable list is customers who have completed at least one paid wash with you — that's the moment someone joins your customer list. A person who scanned your QR but never washed can't be broadcast to.

## What "Send now" actually does

The send goes out immediately, and every recipient passes three gates that are there for _their_ protection:

- Opted out of marketing? **Skipped.** Their choice is always honoured.
- Inside their quiet hours (22:00–08:00 unless they changed it)? **Skipped.** Marketing never pierces quiet hours.
- Never allowed notifications on their phone? **Skipped** — there's nothing to deliver to.

Afterwards you get a summary that names every bucket: sent, delivered, skipped for preferences, skipped for quiet hours, skipped for no permission, failed. So when "All customers" reaches 60% of your list, you can see exactly why — no mystery.

## Limits that keep you out of trouble

- **10 broadcasts per day** per business. Enough for real marketing; too few for spam.
- One send handles up to **10,000 recipients**. A bigger audience gets processed up to the cap and the summary says so plainly — never a silent cut.
- A sent campaign is locked and can't fire twice. To send the same message again, compose a new campaign.

## Honest limits

Sends are **now-only** — there's no scheduling for a future time yet. Push is the **only channel** — no WhatsApp or SMS broadcasts. And a broadcast goes out in **one language**: the title and message you type are exactly what every recipient reads; nothing is auto-translated yet.
